import React, { useState, useCallback, useRef } from 'react';
import { Location, MusicTrack } from '../types';
import { AudioEngine } from '../hooks/useAudioEngine';
import { AmbientPanel } from './AmbientPanel';
import './ScenePlayer.css';

interface Props {
  location: Location;
  audioEngine: AudioEngine;
  onBack: () => void;
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '255,255,255';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

export function ScenePlayer({ location, audioEngine, onBack }: Props) {
  const [activeTrackId, setActiveTrackId] = useState<string | null>(null);
  const [musicVolume, setMusicVolumeState] = useState(0.7);
  const [isVictoryPlaying, setIsVictoryPlaying] = useState(false);
  const [isDeathPlaying, setIsDeathPlaying] = useState(false);
  const postVictoryFileRef = useRef<string | null>(null);

  const exploreTracks = location.musicTracks.filter(t => t.type === 'explore');
  const combatTracks  = location.musicTracks.filter(t => t.type === 'combat');
  const victoryTrack  = location.musicTracks.find(t => t.type === 'victory');

  const playTrack = useCallback((track: MusicTrack) => {
    setActiveTrackId(track.id);
    audioEngine.playMusic(track.file, musicVolume);
  }, [audioEngine, musicVolume]);

  const handleTrackSelect = useCallback((track: MusicTrack) => {
    if (isVictoryPlaying || isDeathPlaying) return;
    playTrack(track);
  }, [isVictoryPlaying, isDeathPlaying, playTrack]);

  const handleVictory = useCallback(() => {
    if (!victoryTrack || isVictoryPlaying || isDeathPlaying) return;

    const currentTrack = location.musicTracks.find(t => t.id === activeTrackId);
    const returnTrack = currentTrack?.type === 'combat'
      ? location.musicTracks.find(t => t.type === 'explore')
      : currentTrack;

    postVictoryFileRef.current = returnTrack?.file || null;
    setIsVictoryPlaying(true);

    audioEngine.playMusic(victoryTrack.file, musicVolume, () => {
      setIsVictoryPlaying(false);
      if (postVictoryFileRef.current) {
        audioEngine.playMusic(postVictoryFileRef.current, musicVolume);
        const ret = location.musicTracks.find(t => t.file === postVictoryFileRef.current);
        if (ret) setActiveTrackId(ret.id);
      }
    });

    setActiveTrackId(victoryTrack.id);
  }, [victoryTrack, isVictoryPlaying, isDeathPlaying, activeTrackId, location.musicTracks, audioEngine, musicVolume]);

  const handlePlayerDeath = useCallback(() => {
    if (isDeathPlaying) return;
    setIsDeathPlaying(true);
    audioEngine.playPlayerDeath(() => {
      setIsDeathPlaying(false);
    });
  }, [isDeathPlaying, audioEngine]);

  const handleMusicVolumeChange = useCallback((vol: number) => {
    setMusicVolumeState(vol);
    audioEngine.setMusicVolume(vol);
  }, [audioEngine]);

  const accentRgb = hexToRgb(location.accentColor);

  return (
    <div
      className="scene-player"
      style={{
        '--accent': location.accentColor,
        '--accent-rgb': accentRgb,
        '--gradient': location.gradient,
      } as React.CSSProperties}
    >
      <div className="player-bg" />
      <div className="player-bg-fade" />

      <header className="player-header">
        <button className="back-btn" onClick={onBack} aria-label="Back">
          ←
        </button>
        <div className="player-title-group">
          <span className="player-emoji">{location.emoji}</span>
          <h1 className="player-title">{location.name}</h1>
        </div>
        <div className="header-spacer" />
      </header>

      <div className="player-content">

        {/* ── Music Section ── */}
        <section className="music-section">
          <div className="section-label">Music</div>

          <div className="volume-row">
            <span className="volume-icon">🎵</span>
            <input
              type="range"
              className="volume-slider"
              min={0} max={1} step={0.01}
              value={musicVolume}
              onChange={e => handleMusicVolumeChange(Number(e.target.value))}
            />
            <span className="volume-pct">{Math.round(musicVolume * 100)}%</span>
          </div>

          {exploreTracks.length > 0 && (
            <div className="track-group">
              <div className="track-group-label">Explore</div>
              <div className="track-buttons">
                {exploreTracks.map(track => (
                  <TrackButton
                    key={track.id}
                    track={track}
                    isActive={activeTrackId === track.id && !isVictoryPlaying && !isDeathPlaying}
                    disabled={isDeathPlaying}
                    onClick={() => handleTrackSelect(track)}
                  />
                ))}
              </div>
            </div>
          )}

          {combatTracks.length > 0 && (
            <div className="track-group">
              <div className="track-group-label">Combat</div>
              <div className="track-buttons">
                {combatTracks.map(track => (
                  <TrackButton
                    key={track.id}
                    track={track}
                    isActive={activeTrackId === track.id && !isVictoryPlaying && !isDeathPlaying}
                    disabled={isDeathPlaying}
                    onClick={() => handleTrackSelect(track)}
                  />
                ))}
              </div>
            </div>
          )}

          {victoryTrack && (
            <button
              className={`victory-btn ${isVictoryPlaying ? 'playing' : ''}`}
              onClick={handleVictory}
              disabled={isVictoryPlaying || isDeathPlaying}
            >
              <span>🏆</span>
              <span>{isVictoryPlaying ? 'Victory Playing…' : 'Victory Fanfare'}</span>
            </button>
          )}
        </section>

        <div className="section-divider" />

        {/* ── Ambience Section ── */}
        <section className="ambient-section">
          <div className="section-label">Ambience</div>
          <AmbientPanel
            sounds={location.ambientSounds}
            audioEngine={audioEngine}
            disabled={isDeathPlaying}
          />
        </section>

        {/* ── Player Death ── */}
        <div className="death-zone">
          <button
            className={`death-btn ${isDeathPlaying ? 'playing' : ''}`}
            onClick={handlePlayerDeath}
            disabled={isDeathPlaying}
          >
            <span className="death-icon">💀</span>
            <span>{isDeathPlaying ? 'A Hero Falls…' : 'Player Death'}</span>
          </button>
          {isDeathPlaying && (
            <p className="death-hint">All sounds will resume when the track ends</p>
          )}
        </div>

      </div>
    </div>
  );
}

function TrackButton({ track, isActive, disabled, onClick }: {
  track: MusicTrack;
  isActive: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`track-btn ${isActive ? 'active' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {isActive && <span className="track-playing-dot" />}
      <span className="track-label">{track.label}</span>
    </button>
  );
}
